import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Rectangle, useMap } from 'react-leaflet';
import L from 'leaflet';
import { QRCodeSVG } from 'qrcode.react';
import 'leaflet/dist/leaflet.css';
import '../styles/Rondes.css';
import apiClient from '../services/api';

const API_URL = (window.__BACKEND_URL__ || process.env.REACT_APP_BACKEND_URL) || '';

// Fix Leaflet default icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom map center helper
function MapCenter({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 16);
    }
  }, [center, map]);
  return null;
}

// Custom icons as divIcons to match design (colors/status)
const iconFactory = (bgColor, content) => L.divIcon({
  className: 'rondes-custom-icon',
  html: `<div style="background:${bgColor};width:24px;height:24px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4);font-size:12px;color:#fff;">${content}</div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const ICON_COMPLETED = iconFactory('#2ecc71', '✓');
const ICON_PENDING = iconFactory('#f1c40f', '⏱');
const ICON_CURRENT = iconFactory('#3498db', '●');
const ICON_MISSED = iconFactory('#e74c3c', '✕');

const Rondes = () => {
  const { user } = useAuth();
  const { toast } = useToast();

  // Backend data
  const [points, setPoints] = useState([]);
  const [tournees, setTournees] = useState([]);
  const [rondes, setRondes] = useState([]);
  const [stats, setStats] = useState(null);
  const [sites, setSites] = useState([]);

  // UI / app state
  const [loading, setLoading] = useState(true);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [mapCenter, setMapCenter] = useState([48.8566, 2.3522]);

  // Ronde en cours (logique inspirée de votre script HTML)
  const [roundStarted, setRoundStarted] = useState(false);
  const [currentPointIndex, setCurrentPointIndex] = useState(0);
  const [completedPointsIds, setCompletedPointsIds] = useState([]);
  const [reports, setReports] = useState([]);

  // Modales / formulaires
  const [showManualModal, setShowManualModal] = useState(false);
  const [showObservationModal, setShowObservationModal] = useState(false);
  const [manualPointId, setManualPointId] = useState('');
  const [manualObservations, setManualObservations] = useState('');
  const [currentObservationPoint, setCurrentObservationPoint] = useState(null);
  const [observationText, setObservationText] = useState('');

  // Nouveau point de contrôle (création via backend)
  const [newPoint, setNewPoint] = useState({
    nom: '',
    siteId: '',
    type: 'surete',
    emplacement: '',
    description: '',
    validationRadius: 50,
    useGPS: false,
  });
  const [showAddPointModal, setShowAddPointModal] = useState(false);

  // Infos sur l'agent et type de ronde
  const [agentRole, setAgentRole] = useState('securite'); // securite, ssiap, maitre_chien
  const [signature, setSignature] = useState('');
  const [exportMonth, setExportMonth] = useState(new Date().toISOString().slice(0, 7));
  const [exporting, setExporting] = useState(false);

  // Notification locale (bandeau vert en haut à droite)
  const [notification, setNotification] = useState('');

  // NFC réel (Web NFC - Chrome Android)
  const [nfcSupported] = useState(() => typeof window !== 'undefined' && 'NDEFReader' in window);
  const [nfcScanning, setNfcScanning] = useState(false);
  const nfcAbortRef = useRef(null);
  const nfcHandlerRef = useRef(null);

  // Encodage badge NFC
  const [showWriteModal, setShowWriteModal] = useState(false);
  const [writePointId, setWritePointId] = useState('');
  const [nfcWriting, setNfcWriting] = useState(false);

  // Charger données backend + position GPS
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const headers = {};
        const opts = { headers, credentials: 'include' };

        const [pointsRes, tourneesRes, rondesRes, statsRes, sitesRes] = await Promise.all([
          fetch(`${API_URL}/api/rondes/points-controle`, opts),
          fetch(`${API_URL}/api/rondes/tournees`, opts),
          fetch(`${API_URL}/api/rondes/effectuees?limit=50`, opts),
          fetch(`${API_URL}/api/rondes/stats`, opts),
          fetch(`${API_URL}/api/sites/`, opts),
        ]);

        if (pointsRes.ok) {
          const data = await pointsRes.json();
          setPoints(data);
          const withCoords = data.find((p) => p.coordinates);
          if (withCoords) {
            setMapCenter([withCoords.coordinates.lat, withCoords.coordinates.lng]);
          }
        }
        if (tourneesRes.ok) setTournees(await tourneesRes.json());
        if (rondesRes.ok) setRondes(await rondesRes.json());
        if (statsRes.ok) setStats(await statsRes.json());
        if (sitesRes.ok) setSites(await sitesRes.json());
      } catch (e) {
        console.error('Erreur chargement rondes:', e);
      } finally {
        setLoading(false);
      }
    };

    loadData();

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setCurrentPosition(coords);
        },
        (err) => {
          console.log('Erreur GPS:', err);
        }
      );
    }
  }, []);

  const totalPoints = points.length;
  const completedCount = completedPointsIds.length;
  const completionRate = totalPoints ? Math.round((completedCount / totalPoints) * 100) : 0;

  const refreshData = async () => {
    try {
      const opts = { credentials: 'include' };
      const [pointsRes, tourneesRes, rondesRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/rondes/points-controle`, opts),
        fetch(`${API_URL}/api/rondes/tournees`, opts),
        fetch(`${API_URL}/api/rondes/effectuees?limit=50`, opts),
        fetch(`${API_URL}/api/rondes/stats`, opts),
      ]);
      if (pointsRes.ok) setPoints(await pointsRes.json());
      if (tourneesRes.ok) setTournees(await tourneesRes.json());
      if (rondesRes.ok) setRondes(await rondesRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      showNotification('Données actualisées.');
    } catch (e) {
      console.error(e);
    }
  };

  // Gestion création point (backend existant)
  const handleCreatePoint = async () => {
    if (!newPoint.nom || !newPoint.siteId) {
      toast({ title: 'Erreur', description: 'Nom et site requis', variant: 'destructive' });
      return;
    }
    try {
      const payload = {
        ...newPoint,
        elementsAVerifier: ['Vérification effectuée'],
        coordinates: newPoint.useGPS && currentPosition ? currentPosition : null,
      };
      delete payload.useGPS;

      const res = await fetch(`${API_URL}/api/rondes/points-controle`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast({ title: 'Succès', description: 'Point de contrôle créé' });
        setShowAddPointModal(false);
        setNewPoint({ nom: '', siteId: '', type: 'surete', emplacement: '', description: '', validationRadius: 50, useGPS: false });
        refreshData();
      } else {
        toast({ title: 'Erreur', description: "Création impossible", variant: 'destructive' });
      }
    } catch (e) {
      console.error(e);
      toast({ title: 'Erreur', description: "Erreur lors de la création", variant: 'destructive' });
    }
  };

  // Mise à jour GPS d'un point existant
  const updatePointGPS = async (pointId) => {
    if (!currentPosition) {
      toast({ title: 'Erreur', description: 'Position GPS non disponible', variant: 'destructive' });
      return;
    }
    try {
      await fetch(`${API_URL}/api/rondes/points-controle/${pointId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ coordinates: currentPosition }),
      });
      showNotification('Position GPS enregistrée pour ce point.');
      refreshData();
    } catch (e) {
      console.error(e);
      toast({ title: 'Erreur', description: 'Impossible de mettre à jour la position', variant: 'destructive' });
    }
  };

  // Démarrer / arrêter la ronde (logique locale + enregistrement backend)
  const startRound = () => {
    if (points.length === 0) {
      showNotification('Aucun point de contrôle défini.');
      return;
    }
    setRoundStarted(true);
    setCurrentPointIndex(0);
    setCompletedPointsIds([]);
    setReports([]);
    setSignature('');
    showNotification('Ronde démarrée ! Allez au premier point de contrôle.');
  };

  const finishRoundAndSave = async () => {
    if (!roundStarted) return;
    try {
      const headers = {
        'Content-Type': 'application/json',
      };
      // Pour l'instant, on prend la première tournée et le premier site comme exemple
      const tournee = tournees[0];
      const site = sites.find((s) => s.id === tournee?.siteId);
      if (!tournee || !site) {
        showNotification('Impossible de déterminer la tournée ou le site.');
        return;
      }

      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10);
      const heureDebut = '00:00';
      const heureFin = `${String(now.getHours()).padStart(2, '0')}:${String(
        now.getMinutes()
      ).padStart(2, '0')}`;

      const pointsControles = completedPointsIds.map((id) => {
        const p = points.find((pt) => pt.id === id);
        return {
          pointControleId: p.id,
          pointControleNom: p.nom,
          heurePassage: heureFin,
          conforme: true,
          observations: '',
          anomaliesDetectees: [],
          photosPrises: [],
          elementsVerifies: {},
          validationType: 'manual',
          scannedCode: p.qrCode || p.nfcTagId || null,
          gpsCoordinates: currentPosition || null,
          gpsAccuracy: null,
          validationTimestamp: now.toISOString(),
        };
      });

      const payload = {
        tourneeId: tournee.id,
        siteId: site.id,
        date: dateStr,
        heureDebut,
        heureFin,
        resourceType: 'agent',
        agentId: user?.id || null,
        agentName: user?.fullName || user?.email || 'Agent',
        subcontractorId: null,
        subcontractorName: null,
        pointsControles,
        anomaliesMajeures: false,
        nombreAnomalies: 0,
        observations: signature || '',
        actionsImmediates: null,
        meteo: null,
        incidents: null,
        agentRole,
      };

      const res = await fetch(`${API_URL}/api/rondes/effectuees`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        showNotification('Ronde enregistrée avec succès.');
        setRoundStarted(false);
        refreshData();
      } else {
        console.error('Erreur enregistrement ronde', await res.text());
        showNotification("Erreur lors de l'enregistrement de la ronde.");
      }
    } catch (e) {
      console.error(e);
      showNotification("Erreur inattendue lors de l'enregistrement de la ronde.");
    }
  };

  const stopRound = () => {
    if (!roundStarted) return;
    setRoundStarted(false);
    // On pourrait marquer les points restants comme "missed" en base plus tard
    showNotification('Ronde arrêtée. Les points non complétés sont considérés comme manqués.');
  };

  // Distance GPS en mètres
  const getDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // Statut visuel d'un point (pending, current, completed, missed)
  const getPointStatus = (index, point) => {
    if (!roundStarted) return 'pending';
    if (completedPointsIds.includes(point.id)) return 'completed';
    if (index === currentPointIndex) return 'current';
    return 'pending';
  };

  const handleValidatePoint = (pointIndex, observations = '') => {
    if (!roundStarted) return;
    const point = points[pointIndex];
    if (!point) return;

    // Si coordonnées disponibles, vérifier la distance
    if (point.coordinates && currentPosition) {
      const d = getDistance(
        currentPosition.lat,
        currentPosition.lng,
        point.coordinates.lat,
        point.coordinates.lng
      );
      const radius = point.validationRadius || 50;
      if (d > radius) {
        toast({
          title: 'Trop loin du point',
          description: `Distance actuelle ${Math.round(d)}m (max ${radius}m)`,
          variant: 'destructive',
        });
        return;
      }
    }

    // Enregistrer un "rapport" local comme dans votre script
    const newReport = {
      point: point.nom,
      time: new Date().toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
      }),
      status: 'OK',
      observations: observations || 'RAS',
    };
    setReports((prev) => [...prev, newReport]);

    setCompletedPointsIds((prev) => [...prev, point.id]);

    if (pointIndex < points.length - 1) {
      setCurrentPointIndex(pointIndex + 1);
    } else {
      // Fin de ronde
      setRoundStarted(false);
      showNotification('Félicitations ! Ronde terminée avec succès.');
    }
  };

  // Simulation du scanner (ouvre la modale d'observation pour le point courant)
  const simulateScan = () => {
    if (!roundStarted) {
      showNotification("Veuillez d'abord démarrer la ronde.");
      return;
    }
    const point = points[currentPointIndex];
    if (!point) return;
    setCurrentObservationPoint(point);
    setObservationText('');
    setShowObservationModal(true);
  };

  // Saisie manuelle (ID de point = qrCode ou nfcTagId)
  const handleManualValidate = () => {
    if (!manualPointId) {
      showNotification("Veuillez entrer un ID de point.");
      return;
    }
    const idx = points.findIndex(
      (p) => p.qrCode === manualPointId || p.nfcTagId === manualPointId
    );
    if (idx === -1) {
      showNotification('Point de contrôle non trouvé.');
      return;
    }
    handleValidatePoint(idx, manualObservations);
    setShowManualModal(false);
    setManualPointId('');
    setManualObservations('');
  };

  // Notification visuelle locale + toast global
  const showNotification = (message) => {
    setNotification(message);
    toast({ title: message });
    setTimeout(() => setNotification(''), 2500);
  };

  // ----- Scan NFC réel (Web NFC API) -----
  const handleNfcRead = (scannedCode) => {
    if (!scannedCode) {
      toast({ title: 'Badge NFC vide', description: 'Aucune donnée lisible sur ce badge', variant: 'destructive' });
      return;
    }
    const idx = points.findIndex(
      (p) => p.nfcTagId === scannedCode || p.qrCode === scannedCode || p.id === scannedCode
    );
    if (idx === -1) {
      toast({
        title: 'Badge inconnu',
        description: `Code lu: ${scannedCode} — aucun point de contrôle correspondant`,
        variant: 'destructive',
      });
      return;
    }
    if (completedPointsIds.includes(points[idx].id)) {
      showNotification(`Point "${points[idx].nom}" déjà validé.`);
      return;
    }
    handleValidatePoint(idx, `Validé par scan NFC (${scannedCode})`);
  };

  // Toujours garder la dernière version du handler (évite les stale closures)
  useEffect(() => {
    nfcHandlerRef.current = handleNfcRead;
  });

  const stopNFCScan = () => {
    if (nfcAbortRef.current) {
      nfcAbortRef.current.abort();
      nfcAbortRef.current = null;
    }
    setNfcScanning(false);
    showNotification('Scan NFC arrêté.');
  };

  const startNFCScan = async () => {
    if (!roundStarted) {
      showNotification("Veuillez d'abord démarrer la ronde.");
      return;
    }
    try {
      const ndef = new window.NDEFReader();
      const controller = new AbortController();
      nfcAbortRef.current = controller;
      await ndef.scan({ signal: controller.signal });
      setNfcScanning(true);
      showNotification('Scan NFC actif — approchez le téléphone du badge...');

      ndef.onreading = (event) => {
        let scanned = event.serialNumber || '';
        try {
          for (const record of event.message.records) {
            if (record.recordType === 'text') {
              scanned = new TextDecoder(record.encoding || 'utf-8').decode(record.data);
            } else if (record.recordType === 'url' && !scanned) {
              scanned = new TextDecoder().decode(record.data);
            }
          }
        } catch (e) {
          console.warn('Lecture NDEF partielle:', e);
        }
        if (nfcHandlerRef.current) nfcHandlerRef.current(String(scanned).trim());
      };

      ndef.onreadingerror = () => {
        toast({ title: 'Erreur NFC', description: 'Lecture du badge impossible, réessayez', variant: 'destructive' });
      };
    } catch (e) {
      setNfcScanning(false);
      nfcAbortRef.current = null;
      const msg = e.name === 'NotAllowedError'
        ? 'Autorisation NFC refusée. Activez le NFC dans les paramètres.'
        : e.message;
      toast({ title: 'NFC indisponible', description: msg, variant: 'destructive' });
    }
  };

  // Arrêt du scan NFC au démontage du composant
  useEffect(() => {
    return () => {
      if (nfcAbortRef.current) nfcAbortRef.current.abort();
    };
  }, []);

  // ----- Encodage d'un badge NFC vierge (Web NFC write) -----
  const handleWriteBadge = async () => {
    const point = points.find((p) => p.id === writePointId);
    if (!point) {
      showNotification('Sélectionnez un point de contrôle.');
      return;
    }
    let code = point.nfcTagId;
    setNfcWriting(true);
    try {
      if (!code) {
        code = `NFC-${String(point.id).replace(/-/g, '').slice(0, 8).toUpperCase()}`;
        const res = await fetch(`${API_URL}/api/rondes/points-controle/${point.id}`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nfcTagId: code }),
        });
        if (!res.ok) throw new Error("Impossible d'attribuer un identifiant NFC au point");
      }
      const ndef = new window.NDEFReader();
      showNotification('Approchez le badge NFC vierge du téléphone...');
      await ndef.write({ records: [{ recordType: 'text', data: code }] });
      showNotification(`Badge encodé avec succès: ${code}`);
      setShowWriteModal(false);
      setWritePointId('');
      refreshData();
    } catch (e) {
      const msg = e.name === 'NotAllowedError'
        ? 'Autorisation NFC refusée. Activez le NFC dans les paramètres.'
        : e.message;
      toast({ title: 'Écriture NFC échouée', description: msg, variant: 'destructive' });
    } finally {
      setNfcWriting(false);
    }
  };

  // Données dérivées pour les stats inspirées de la maquette
  const siteBounds = [
    [48.855, 2.35],
    [48.858, 2.355],
  ];

  if (loading) {
    return (
      <div className="rondes-page min-h-screen flex items-center justify-center">
        <div className="rondes-loader" />
      </div>
    );
  }

  return (
    <div className="rondes-page">
      <div className="container">
        <header>
          <h1>
            <i className="fas fa-shield-alt" /> Système de Rondes de Gardiennage
          </h1>
          <p className="subtitle">
            Gestion des points de contrôle NFC, QR Code et géolocalisation
          </p>
        </header>

        <div className="dashboard">
          {/* Colonne gauche : itinéraire & liste des points */}
          <div className="card">
            <h2 className="card-title">
              <i className="fas fa-map-marked-alt" /> Itinéraire de la Ronde
            </h2>

            <div className="route-info">
              <div>
                <strong>Départ:</strong> {sites[0]?.name || 'Poste de Sécurité A'}
              </div>
              <div>
                <strong>Fin:</strong> Poste de Contrôle Final
              </div>
            </div>

            <div className="route-info">
              <div>
                <strong>Type d&apos;agent:</strong>{' '}
                <select
                  value={agentRole}
                  onChange={(e) => setAgentRole(e.target.value)}
                  className="bg-transparent border border-gray-500 rounded px-2 py-1 text-sm ml-2"
                >
                  <option value="securite">Agent de sécurité</option>
                  <option value="ssiap">Agent SSIAP</option>
                  <option value="maitre_chien">Maître-chien</option>
                </select>
              </div>
              <div>
                <strong>Signature agent:</strong>{' '}
                <input
                  type="text"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder="Nom, mention, etc."
                  className="bg-transparent border border-gray-500 rounded px-2 py-1 text-sm ml-2"
                />
              </div>
            </div>

            <div className="route-info">
              <div>
                <strong>Total Points:</strong>{' '}
                <span id="total-points">{totalPoints || 0}</span>
              </div>
              <div>
                <strong>Distance:</strong> 1.2 km
              </div>
            </div>

            <div className="progress-container">
              <div>Progression de la Ronde</div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${completionRate}%` }}
                  id="progress-fill"
                />
              </div>
              <div style={{ textAlign: "right", marginTop: 5 }}>
                <span id="completed-count">{completedCount}</span>
                /
                <span id="total-count">{totalPoints}</span> points complétés
              </div>
            </div>

            <div className="points-list" id="points-list">
              {points.length === 0 && (
                <p className="rondes-empty-text">
                  Aucun point de contrôle pour l&apos;instant. Ajoutez-en un pour démarrer une
                  ronde.
                </p>
              )}
              {points.map((p, idx) => {
                const status = getPointStatus(idx, p);
                const classes = ['point-item'];
                if (status === 'completed') classes.push('completed');
                if (status === 'current') classes.push('current');
                if (status === 'missed') classes.push('missed');

                return (
                  <div key={p.id || idx} className={classes.join(' ')}>
                    <div className="point-info">
                      <h3>
                        {p.type === 'incendie' ? (
                          <i className="fas fa-fire-extinguisher" />
                        ) : (
                          <i className="fas fa-user-shield" />
                        )}{' '}
                        {p.nom}
                      </h3>
                      <p>
                        ID: {p.qrCode || p.nfcTagId || '—'} | Type:{' '}
                        {p.type?.toUpperCase() || 'N/A'}
                      </p>
                      <p>{p.emplacement}</p>
                    </div>
                    <div className="point-actions">
                      {!p.coordinates && (
                        <button
                          className="btn btn-secondary"
                          type="button"
                          onClick={() => updatePointGPS(p.id)}
                        >
                          <i className="fas fa-location-arrow" /> GPS
                        </button>
                      )}
                      <span
                        className={`status ${
                          status === 'completed'
                            ? 'status-completed'
                            : status === 'current'
                            ? 'status-current'
                            : status === 'missed'
                            ? 'status-missed'
                            : 'status-pending'
                        }`}
                      >
                        {status === 'completed'
                          ? 'Complété'
                          : status === 'current'
                          ? 'En cours'
                          : status === 'missed'
                          ? 'Manqué'
                          : 'En attente'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="controls">
              <button
                className={`btn ${roundStarted ? 'btn-warning' : 'btn-primary'}`}
                type="button"
                onClick={() => (roundStarted ? stopRound() : startRound())}
                id="start-round"
              >
                <i className={roundStarted ? 'fas fa-pause' : 'fas fa-play'} />
                {roundStarted ? ' Pause' : ' Démarrer Ronde'}
              </button>
              <button
                className="btn btn-danger"
                type="button"
                onClick={stopRound}
                id="stop-round"
              >
                <i className="fas fa-stop" /> Arrêter Ronde
              </button>
              {roundStarted && (
                <button
                  className="btn btn-success"
                  type="button"
                  onClick={finishRoundAndSave}
                >
                  <i className="fas fa-save" /> Enregistrer la ronde
                </button>
              )}
              <button
                className="btn btn-secondary"
                type="button"
                onClick={refreshData}
                id="refresh-data"
              >
                <i className="fas fa-sync" /> Actualiser
              </button>
            </div>

            <div className="controls" style={{ marginTop: 15 }}>
              <button
                className="btn btn-success"
                type="button"
                onClick={() => setShowAddPointModal(true)}
              >
                <i className="fas fa-plus" /> Ajouter Point
              </button>
            </div>
          </div>

          {/* Colonne droite : Carte */}
          <div className="card">
            <h2 className="card-title">
              <i className="fas fa-globe-europe" /> Carte de Géolocalisation
            </h2>
            <div id="map" className="rondes-map-container">
              <MapContainer
                center={mapCenter}
                zoom={16}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution="&copy; OpenStreetMap contributors"
                />
                <MapCenter center={mapCenter} />

                {/* Rectangle du site comme dans votre script */}
                <Rectangle
                  bounds={siteBounds}
                  pathOptions={{ color: '#3498db', weight: 2, fillOpacity: 0.1 }}
                />

                {/* Position de l'agent */}
                {currentPosition && (
                  <Marker
                    position={[currentPosition.lat, currentPosition.lng]}
                    icon={ICON_CURRENT}
                  >
                    <Popup>
                      <b>Votre position</b>
                    </Popup>
                  </Marker>
                )}

                {/* Marqueurs des points */}
                {points.map((p, idx) => {
                  if (!p.coordinates) return null;
                  const status = getPointStatus(idx, p);
                  const icon =
                    status === 'completed'
                      ? ICON_COMPLETED
                      : status === 'current'
                      ? ICON_CURRENT
                      : ICON_PENDING;

                  return (
                    <Marker
                      key={p.id || idx}
                      position={[p.coordinates.lat, p.coordinates.lng]}
                      icon={icon}
                    >
                      <Popup>
                        <b>{p.nom}</b>
                        <br />
                        Status: {' '}
                        {status === 'completed'
                          ? 'Complété'
                          : status === 'current'
                          ? 'En cours'
                          : 'En attente'}
                        <br />
                        ID: {p.qrCode || p.nfcTagId || '—'} | Type:{' '}
                        {p.type?.toUpperCase() || 'N/A'}
                      </Popup>
                    </Marker>
                  );
                })}

                {/* Tracé de l'itinéraire entre les points géolocalisés */}
                {points.filter((p) => p.coordinates).length > 1 && (
                  <Polyline
                    positions={points
                      .filter((p) => p.coordinates)
                      .map((p) => [p.coordinates.lat, p.coordinates.lng])}
                    pathOptions={{ color: '#3498db', weight: 4, dashArray: '10, 10' }}
                  />
                )}
              </MapContainer>
            </div>

            <div className="controls">
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => {
                  if (currentPosition) {
                    setMapCenter([currentPosition.lat, currentPosition.lng]);
                    showNotification('Position centrée sur votre localisation.');
                  } else {
                    showNotification('Position GPS non disponible.');
                  }
                }}
                id="center-position"
              >
                <i className="fas fa-location-arrow" /> Centrer sur ma Position
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => showNotification('Itinéraire affiché sur la carte.')}
                id="show-route"
              >
                <i className="fas fa-route" /> Afficher Itinéraire
              </button>
              <button
                className="btn btn-success"
                type="button"
                onClick={() => showNotification('Rapport PDF généré avec succès !')}
                id="export-report"
              >
                <i className="fas fa-file-pdf" /> Export PDF
              </button>
            </div>
          </div>
        </div>

        {/* Scanner + stats */}
        <div className="dashboard">
          <div className="card">
            <h2 className="card-title">
              <i className="fas fa-qrcode" /> Scanner NFC / QR Code
            </h2>
            <div className="scanner-section">
              <div
                className="scanner-placeholder"
                id="scanner-trigger"
                onClick={simulateScan}
                style={nfcScanning ? { borderColor: '#2ecc71', boxShadow: '0 0 12px rgba(46,204,113,0.5)' } : undefined}
              >
                <div className="scanner-line" />
                <i className={nfcScanning ? 'fas fa-wifi' : 'fas fa-camera'} />
              </div>
              <p>
                {nfcScanning
                  ? 'Scan NFC actif — approchez le téléphone du badge du point de contrôle'
                  : nfcSupported
                    ? 'Utilisez le scan NFC réel ou cliquez sur le scanner pour une validation simulée'
                    : "Cliquez sur le scanner pour simuler la lecture d'un point de contrôle"}
              </p>
              <div className="controls">
                {nfcSupported && (
                  <button
                    className={nfcScanning ? 'btn btn-danger' : 'btn btn-success'}
                    type="button"
                    onClick={nfcScanning ? stopNFCScan : startNFCScan}
                    data-testid="nfc-scan-btn"
                  >
                    <i className="fas fa-wifi" /> {nfcScanning ? 'Arrêter le scan NFC' : 'Scanner NFC réel'}
                  </button>
                )}
                {nfcSupported && (
                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={() => setShowWriteModal(true)}
                    data-testid="nfc-write-btn"
                  >
                    <i className="fas fa-pen-nib" /> Encoder Badge NFC
                  </button>
                )}
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => setShowManualModal(true)}
                  id="manual-entry"
                >
                  <i className="fas fa-keyboard" /> Saisie Manuelle
                </button>
              </div>
              {!nfcSupported && (
                <p style={{ fontSize: 12, opacity: 0.65, marginTop: 8 }}>
                  <i className="fas fa-info-circle" /> Scan NFC réel disponible sur Chrome Android (app installée). Sur iOS, utilisez la saisie manuelle ou le QR code.
                </p>
              )}
            </div>
          </div>

          <div className="card">
            <h2 className="card-title">
              <i className="fas fa-chart-line" /> Statistiques & Rapport
            </h2>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value" id="completion-rate">
                  {completionRate}%
                </div>
                <div className="stat-label">Taux de Completion</div>
              </div>
              <div className="stat-card">
                <div className="stat-value" id="rounds-today">
                  {stats?.total_aujourd_hui ?? 0}
                </div>
                <div className="stat-label">Rondes Aujourd&apos;hui</div>
              </div>
              <div className="stat-card">
                <div className="stat-value" id="missed-points">
                  {stats?.points_manques ?? 0}
                </div>
                <div className="stat-label">Points Manqués</div>
              </div>
              <div className="stat-card">
                <div className="stat-value" id="avg-time">
                  {stats?.temps_moyen ?? '0min'}
                </div>
                <div className="stat-label">Temps Moyen</div>
              </div>
            </div>

            <div className="report-section">
              <h3>
                <i className="fas fa-clipboard-list" /> Dernier Rapport de Contrôle
              </h3>
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Point</th>
                    <th>Heure</th>
                    <th>Status</th>
                    <th>Observations</th>
                  </tr>
                </thead>
                <tbody id="report-body">
                  {reports.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'center', opacity: 0.7 }}>
                        Aucun point validé pour le moment.
                      </td>
                    </tr>
                  )}
                  {reports.map((r, idx) => (
                    <tr key={`${r.point}-${r.time}-${idx}`}>
                      <td>{r.point}</td>
                      <td>{r.time}</td>
                      <td>
                        <span className="status status-completed">OK</span>
                      </td>
                      <td>{r.observations}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Historique des rondes (depuis backend) */}
        <div className="card">
          <h2 className="card-title">
            <i className="fas fa-history" /> Historique des Rondes
          </h2>
          <div className="flex items-center gap-2 mb-3" data-testid="rondes-export-zip">
            <input
              type="month"
              value={exportMonth}
              onChange={(e) => setExportMonth(e.target.value)}
              className="text-sm px-2 py-1 rounded border border-slate-300"
              data-testid="export-month-input"
            />
            <button
              type="button"
              disabled={!exportMonth || exporting}
              onClick={async () => {
                setExporting(true);
                try {
                  const { data } = await apiClient.get(`/rondes/effectuees/export-zip?month=${exportMonth}`, { responseType: 'blob' });
                  const url = URL.createObjectURL(data);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `rondes_${exportMonth}.zip`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch (err) {
                  const s = err?.response?.status;
                  setNotification(s === 404 ? 'Aucune ronde ce mois-ci' : s === 403 ? 'Reserve aux managers' : 'Export impossible');
                  setTimeout(() => setNotification(''), 2500);
                } finally {
                  setExporting(false);
                }
              }}
              className="text-xs px-3 py-1.5 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="export-month-zip-btn"
            >
              <i className="fas fa-file-archive" /> {exporting ? 'Generation...' : 'Rapports du mois (ZIP)'}
            </button>
          </div>
          <div className="timeline" id="timeline">
            {rondes.length === 0 && (
              <p className="rondes-empty-text">Aucune ronde effectuée.</p>
            )}
            {rondes.slice(0, 15).map((ronde, idx) => (
              <div className="timeline-item" key={ronde.id || idx}>
                <div className="timeline-icon">
                  <i
                    className={
                      ronde.conforme ? 'fas fa-check' : 'fas fa-exclamation-triangle'
                    }
                  />
                </div>
                <div className="timeline-content">
                  <div className="timeline-time">
                    {new Date(ronde.date).toLocaleDateString('fr-FR')}{' '}
                    {ronde.heureDebut || ''}
                  </div>
                  <h4>
                    {ronde.tourneeName || 'Ronde'} -{' '}
                    {ronde.conforme ? 'Complétée' : 'Incomplète'}
                  </h4>
                  <p>
                    {ronde.nombrePointsControles}/{ronde.nombrePointsPrevus} points
                    contrôlés
                  </p>
                  {ronde.id && (
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const { data } = await apiClient.get(`/rondes/effectuees/${ronde.id}/signature`);
                            const text = `Verifiez ce rapport sur ${window.location.origin}/verify-ronde\nID ronde: ${data.ronde_id}\nSignature: ${data.signature}`;
                            await navigator.clipboard.writeText(text);
                            setNotification('Hash copie dans le presse-papier');
                            setTimeout(() => setNotification(''), 2500);
                          } catch {
                            setNotification('Impossible de recuperer le hash');
                            setTimeout(() => setNotification(''), 2500);
                          }
                        }}
                        className="text-xs px-2 py-1 rounded border border-cyan-300 text-cyan-700 hover:bg-cyan-50 inline-flex items-center gap-1"
                        data-testid={`copy-hash-${ronde.id}`}
                      >
                        <i className="fas fa-fingerprint" /> Copier hash
                      </button>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/rondes/effectuees/${ronde.id}/qr-verify`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs px-2 py-1 rounded border border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50 inline-flex items-center gap-1"
                        data-testid={`qr-verify-${ronde.id}`}
                      >
                        <i className="fas fa-qrcode" /> QR Verification
                      </a>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const { data } = await apiClient.get(`/rondes/effectuees/${ronde.id}/report-pdf`, { responseType: 'blob' });
                            const url = URL.createObjectURL(data);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `ronde_${ronde.numero || ronde.id}.pdf`;
                            a.click();
                            URL.revokeObjectURL(url);
                          } catch {
                            setNotification('Impossible de generer le PDF');
                            setTimeout(() => setNotification(''), 2500);
                          }
                        }}
                        className="text-xs px-2 py-1 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1"
                        data-testid={`download-pdf-${ronde.id}`}
                      >
                        <i className="fas fa-file-pdf" /> Rapport PDF
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <footer>
          <p>Système de Rondes de Gardiennage © 2024 | Tous droits réservés</p>
        </footer>
      </div>

      {/* Notification flottante */}
      {notification && (
        <div className={`notification show`} id="notification">
          {notification}
        </div>
      )}

      {/* Modal Saisie manuelle */}
      {/* Modal Encodage Badge NFC */}
      {showWriteModal && (
        <div className="modal" id="nfc-write-modal" onClick={() => !nfcWriting && setShowWriteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>
              <i className="fas fa-pen-nib" /> Encoder un Badge NFC
            </h2>
            <div className="input-group">
              <label htmlFor="write-point-select">Point de contrôle à encoder:</label>
              <select
                id="write-point-select"
                value={writePointId}
                onChange={(e) => setWritePointId(e.target.value)}
                data-testid="nfc-write-point-select"
              >
                <option value="">-- Choisir un point --</option>
                {points.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nom} {p.nfcTagId ? `(${p.nfcTagId})` : '(nouvel ID)'}
                  </option>
                ))}
              </select>
            </div>
            <p style={{ fontSize: 13, opacity: 0.75, margin: '8px 0 16px' }}>
              <i className="fas fa-info-circle" /> Choisissez le point, appuyez sur Écrire, puis approchez le badge NFC vierge du téléphone. L&apos;identifiant du point sera gravé sur le badge.
            </p>
            <div className="controls">
              <button
                className="btn btn-success"
                type="button"
                onClick={handleWriteBadge}
                disabled={nfcWriting || !writePointId}
                data-testid="nfc-write-confirm-btn"
              >
                <i className={nfcWriting ? 'fas fa-spinner fa-spin' : 'fas fa-pen-nib'} />{' '}
                {nfcWriting ? 'Approchez le badge...' : 'Écrire sur le badge'}
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => setShowWriteModal(false)}
                disabled={nfcWriting}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {showManualModal && (
        <div className="modal" id="manual-modal" onClick={() => setShowManualModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>
              <i className="fas fa-keyboard" /> Saisie Manuelle
            </h2>
            <div className="input-group">
              <label htmlFor="point-id">ID du Point de Contrôle:</label>
              <input
                id="point-id"
                type="text"
                value={manualPointId}
                onChange={(e) => setManualPointId(e.target.value.toUpperCase())}
                placeholder="Ex: NFC-001 ou QR-001"
              />
            </div>
            <div className="input-group">
              <label htmlFor="observations">Observations:</label>
              <textarea
                id="observations"
                className="observation-input"
                value={manualObservations}
                onChange={(e) => setManualObservations(e.target.value)}
                placeholder="Entrez vos observations..."
              />
            </div>
            <div className="controls">
              <button
                className="btn btn-primary"
                type="button"
                onClick={handleManualValidate}
                id="validate-manual"
              >
                <i className="fas fa-check" /> Valider
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => setShowManualModal(false)}
                id="cancel-manual"
              >
                <i className="fas fa-times" /> Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Observations après scan */}
      {showObservationModal && currentObservationPoint && (
        <div
          className="modal"
          id="observation-modal"
          onClick={() => setShowObservationModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>
              <i className="fas fa-comment" /> Observations
            </h2>
            <p>
              Point : <span id="current-point-name">{currentObservationPoint.nom}</span>
            </p>
            <div className="input-group">
              <label htmlFor="point-observations">Observations:</label>
              <textarea
                id="point-observations"
                className="observation-input"
                value={observationText}
                onChange={(e) => setObservationText(e.target.value)}
                placeholder="Entrez vos observations..."
              />
            </div>
            <div className="controls">
              <button
                className="btn btn-success"
                type="button"
                id="save-observation"
                onClick={() => {
                  handleValidatePoint(currentPointIndex, observationText);
                  setShowObservationModal(false);
                }}
              >
                <i className="fas fa-save" /> Enregistrer
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                id="skip-observation"
                onClick={() => {
                  handleValidatePoint(currentPointIndex, '');
                  setShowObservationModal(false);
                }}
              >
                <i className="fas fa-forward" /> Passer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ajout Point de contrôle (backend) */}
      {showAddPointModal && (
        <div
          className="modal"
          onClick={() => setShowAddPointModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>
              <i className="fas fa-plus-circle" /> Nouveau Point de Contrôle
            </h2>
            <div className="input-group">
              <label>Nom *</label>
              <input
                type="text"
                value={newPoint.nom}
                onChange={(e) => setNewPoint({ ...newPoint, nom: e.target.value })}
                placeholder="Ex: Entrée Principale"
              />
            </div>
            <div className="input-group">
              <label>Site *</label>
              <select
                value={newPoint.siteId}
                onChange={(e) => setNewPoint({ ...newPoint, siteId: e.target.value })}
              >
                <option value="">Sélectionner...</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="input-group">
              <label>Type</label>
              <select
                value={newPoint.type}
                onChange={(e) => setNewPoint({ ...newPoint, type: e.target.value })}
              >
                <option value="surete">Sûreté</option>
                <option value="incendie">Incendie</option>
              </select>
            </div>
            <div className="input-group">
              <label>Emplacement</label>
              <input
                type="text"
                value={newPoint.emplacement}
                onChange={(e) =>
                  setNewPoint({ ...newPoint, emplacement: e.target.value })
                }
                placeholder="Hall, Parking, Escalier..."
              />
            </div>
            <div className="input-group">
              <label>Rayon GPS (m)</label>
              <input
                type="number"
                value={newPoint.validationRadius}
                onChange={(e) =>
                  setNewPoint({
                    ...newPoint,
                    validationRadius: parseInt(e.target.value, 10) || 0,
                  })
                }
              />
            </div>
            {currentPosition && (
              <div className="input-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newPoint.useGPS}
                    onChange={(e) =>
                      setNewPoint({ ...newPoint, useGPS: e.target.checked })
                    }
                  />{' '}
                  Utiliser ma position GPS actuelle
                </label>
              </div>
            )}
            <div className="controls">
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => setShowAddPointModal(false)}
              >
                Annuler
              </button>
              <button
                className="btn btn-primary"
                type="button"
                onClick={handleCreatePoint}
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Rondes;
